import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getApiBase } from '@papan/api-client'
import { useAuth } from '../hooks/useAuth'
import { loginPathWithNext } from '../lib/session-loss'

/**
 * `/sign-in?continue=<Logto URL>` — where Logto's sign-in bridge (apps/api/src/v1/custom-sign-in-ui/index.html,
 * PAPAN_BRIDGE_SIGN_IN_URL) sends a person it found signed out. Without this route the catch-all took a signed-in
 * person to the launcher and the OAuth flow (a third-party app, a remote MCP client) never resumed.
 *
 * Signed out → the normal login, returning here. Signed in → the API's `/api/auth/bridge-continue`, which redirects
 * ONLY to the configured Logto origin (LOGTO_ENDPOINT_URL). The SPA can't judge that origin itself: a white-label
 * host's Logto (auth.gnexis.ai) is no function of its frontend (os.gnexis.ai).
 */
export function bridgeContinueUrl(raw: string | null, apiBase: string): string | null {
  if (!raw) return null
  let u: URL
  try { u = new URL(raw) } catch { return null }
  if (u.username || u.password || !(u.protocol === 'https:' || u.hostname === 'localhost' || u.hostname === '127.0.0.1')) return null
  return `${apiBase}/api/auth/bridge-continue?continue=${encodeURIComponent(u.toString())}`
}

export function LogtoBridgeSignInPage() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading) return
    const target = bridgeContinueUrl(new URLSearchParams(location.search).get('continue'), getApiBase())
    if (!target) {
      navigate('/', { replace: true })
      return
    }
    if (!isAuthenticated) {
      navigate(loginPathWithNext(`${location.pathname}${location.search}`), { replace: true })
      return
    }
    // `target` is always the API's own /api/auth/bridge-continue (bridgeContinueUrl); the server redirects only to LOGTO_ENDPOINT_URL's exact origin
    // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
    window.location.replace(target)
  }, [isAuthenticated, isLoading, location.pathname, location.search, navigate])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#71717a', fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontSize: 14 }}>
      Signing you in…
    </div>
  )
}
